import React from 'react';
import { 
  Calendar, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertCircle,
  PlayCircle 
} from 'lucide-react';

export type AppointmentStatus = 
  | 'agendado' 
  | 'scheduled'
  | 'confirmado' 
  | 'confirmed'
  | 'em_andamento' 
  | 'in_progress'
  | 'concluído' 
  | 'completed'
  | 'cancelado' 
  | 'cancelled'
  | 'atrasado'
  | 'overdue';

export type PaymentStatus = 
  | 'paid'
  | 'pago'
  | 'pending'
  | 'pendente'
  | 'cancelled'
  | 'cancelado';

interface StatusBadgeProps {
  status: AppointmentStatus | PaymentStatus | string;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  className?: string;
}

interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  contrastRatio: number; // Para acessibilidade (WCAG AA requer >= 4.5:1)
}

const statusConfigs: Record<string, StatusConfig> = {
  // Agendado / Scheduled
  agendado: {
    label: 'Agendado',
    color: '#1e40af', // Azul escuro para melhor contraste
    bgColor: '#dbeafe', // Azul claro de fundo
    borderColor: '#3b82f6', // Azul médio para borda
    icon: Calendar,
    contrastRatio: 7.1, // Excelente contraste
  },
  scheduled: {
    label: 'Scheduled',
    color: '#1e40af',
    bgColor: '#dbeafe',
    borderColor: '#3b82f6',
    icon: Calendar,
    contrastRatio: 7.1,
  },
  
  // Confirmado / Confirmed
  confirmado: {
    label: 'Confirmado',
    color: '#166534', // Verde escuro para melhor contraste
    bgColor: '#dcfce7', // Verde claro de fundo
    borderColor: '#22c55e', // Verde médio para borda
    icon: CheckCircle2,
    contrastRatio: 6.8, // Excelente contraste
  },
  confirmed: {
    label: 'Confirmed',
    color: '#166534',
    bgColor: '#dcfce7',
    borderColor: '#22c55e',
    icon: CheckCircle2,
    contrastRatio: 6.8,
  },
  
  // Em Andamento / In Progress
  em_andamento: {
    label: 'Em Andamento',
    color: '#92400e', // Laranja escuro para melhor contraste
    bgColor: '#fef3c7', // Amarelo claro de fundo
    borderColor: '#f59e0b', // Laranja médio para borda
    icon: PlayCircle,
    contrastRatio: 6.2, // Excelente contraste
  },
  in_progress: {
    label: 'In Progress',
    color: '#92400e',
    bgColor: '#fef3c7',
    borderColor: '#f59e0b',
    icon: PlayCircle,
    contrastRatio: 6.2,
  },
  
  // Concluído / Completed
  concluído: {
    label: 'Concluído',
    color: '#065f46', // Verde muito escuro para melhor contraste
    bgColor: '#d1fae5', // Verde claro de fundo
    borderColor: '#10b981', // Verde médio para borda
    icon: CheckCircle2,
    contrastRatio: 7.5, // Excelente contraste
  },
  completed: {
    label: 'Completed',
    color: '#065f46',
    bgColor: '#d1fae5',
    borderColor: '#10b981',
    icon: CheckCircle2,
    contrastRatio: 7.5,
  },
  
  // Cancelado / Cancelled
  cancelado: {
    label: 'Cancelado',
    color: '#991b1b', // Vermelho escuro para melhor contraste
    bgColor: '#fee2e2', // Vermelho claro de fundo
    borderColor: '#ef4444', // Vermelho médio para borda
    icon: XCircle,
    contrastRatio: 7.0, // Excelente contraste
  },
  cancelled: {
    label: 'Cancelled',
    color: '#991b1b',
    bgColor: '#fee2e2',
    borderColor: '#ef4444',
    icon: XCircle,
    contrastRatio: 7.0,
  },
  
  // Atrasado / Overdue
  atrasado: {
    label: 'Atrasado',
    color: '#7c2d12', // Vermelho-alaranjado escuro para melhor contraste
    bgColor: '#fed7aa', // Laranja claro de fundo
    borderColor: '#f97316', // Laranja médio para borda
    icon: AlertCircle,
    contrastRatio: 6.5, // Excelente contraste
  },
  overdue: {
    label: 'Overdue',
    color: '#7c2d12',
    bgColor: '#fed7aa',
    borderColor: '#f97316',
    icon: AlertCircle,
    contrastRatio: 6.5,
  },
  
  // Status de Pagamento
  paid: {
    label: 'Pago',
    color: '#065f46', // Verde muito escuro
    bgColor: '#d1fae5', // Verde claro de fundo
    borderColor: '#10b981', // Verde médio para borda
    icon: CheckCircle2,
    contrastRatio: 7.5,
  },
  pago: {
    label: 'Pago',
    color: '#065f46',
    bgColor: '#d1fae5',
    borderColor: '#10b981',
    icon: CheckCircle2,
    contrastRatio: 7.5,
  },
  pending: {
    label: 'Pendente',
    color: '#92400e', // Laranja escuro
    bgColor: '#fef3c7', // Amarelo claro de fundo
    borderColor: '#f59e0b', // Laranja médio para borda
    icon: Clock,
    contrastRatio: 6.2,
  },
  pendente: {
    label: 'Pendente',
    color: '#92400e',
    bgColor: '#fef3c7',
    borderColor: '#f59e0b',
    icon: Clock,
    contrastRatio: 6.2,
  },
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'medium',
  showIcon = true,
  className = '',
}) => {
  // Normalizar status para minúsculas
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  
  // Obter configuração do status ou usar padrão
  const config = statusConfigs[normalizedStatus] || {
    label: status,
    color: '#6b7280',
    bgColor: '#f3f4f6',
    borderColor: '#9ca3af',
    icon: Clock,
    contrastRatio: 4.5,
  };

  const Icon = config.icon;

  // Tamanhos
  const sizeStyles = {
    small: {
      padding: '2px 8px',
      fontSize: '11px',
      iconSize: 12,
      gap: '4px',
    },
    medium: {
      padding: '4px 12px',
      fontSize: '12px',
      iconSize: 14,
      gap: '6px',
    },
    large: {
      padding: '6px 16px',
      fontSize: '14px',
      iconSize: 16,
      gap: '8px',
    },
  };

  const currentSize = sizeStyles[size];

  return (
    <span
      className={`status-badge status-badge-${normalizedStatus} ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: currentSize.gap,
        padding: currentSize.padding,
        fontSize: currentSize.fontSize,
        fontWeight: 500,
        color: config.color,
        backgroundColor: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        borderRadius: '9999px',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        // Acessibilidade: garantir contraste adequado
        // WCAG AA requer contraste mínimo de 4.5:1 para texto normal
        // Nossas cores têm contraste >= 6.0:1, garantindo excelente acessibilidade
      }}
      title={`Status: ${config.label} (Contraste: ${config.contrastRatio.toFixed(1)}:1)`}
      aria-label={`Status ${config.label}`}
    >
      {showIcon && (
        <Icon 
          size={currentSize.iconSize} 
          className="status-badge-icon"
          aria-hidden="true"
        />
      )}
      <span>{config.label}</span>
    </span>
  );
};

export default StatusBadge;

// Função helper para obter apenas a cor (para compatibilidade com código existente)
export const getStatusColor = (status: AppointmentStatus | string): string => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  return statusConfigs[normalizedStatus]?.color || '#6b7280';
};

// Função helper para obter apenas o label (para compatibilidade)
export const getStatusLabel = (status: AppointmentStatus | string): string => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  return statusConfigs[normalizedStatus]?.label || status;
};

// Função helper para obter ícone (para uso em outros componentes)
export const getStatusIcon = (status: AppointmentStatus | string) => {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  return statusConfigs[normalizedStatus]?.icon || Clock;
};

